import React from 'react';
import { useTranslation } from 'react-i18next';
import Sentry from '../../lib/sentry';
import { Button } from '../ui/button';

/**
 * Fallback UI shown when AppErrorBoundary catches a render crash.
 *
 * `Sentry.ErrorBoundary` has already reported the error and produced
 * `eventId` by the time this renders. Report this crash opens the same
 * Feedback widget configured in lib/sentry.js, tagged with the crash's event
 * id so the two can be correlated in Sentry's dashboard (a tag, not Sentry's
 * native associated_event_id linkage -- that field only exists on the
 * low-level sendFeedback/captureFeedback API, not the pre-built widget's
 * createForm()).
 */
const ErrorFallback = ({ eventId }) => {
  const { t } = useTranslation('common');

  const handleReportCrash = () => {
    Sentry.getFeedback()
      ?.createForm({ tags: { crash_event_id: eventId } })
      .then((form) => {
        form.appendToDom();
        form.open();
      });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('errorBoundary.title')}</h2>
      <p className="text-sm text-gray-600 max-w-md mb-6">{t('errorBoundary.message')}</p>
      <div className="flex gap-3">
        <Button onClick={() => window.location.reload()}>{t('errorBoundary.reload')}</Button>
        {Sentry.getFeedback() && (
          <Button variant="outline" onClick={handleReportCrash}>
            {t('errorBoundary.reportCrash')}
          </Button>
        )}
      </div>
      {eventId && (
        <p className="mt-6 text-xs text-gray-400">{t('errorBoundary.eventId', { eventId })}</p>
      )}
    </div>
  );
};

/**
 * App-level render-crash boundary. Wraps the whole route tree in index.jsx
 * so any component crash on any route is caught, reported to Sentry, and
 * shown a recoverable fallback instead of a blank white screen.
 */
const AppErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary fallback={({ eventId }) => <ErrorFallback eventId={eventId} />}>
    {children}
  </Sentry.ErrorBoundary>
);

export default AppErrorBoundary;
